import React from "react";

//QUERIES

import { useQuery, useQueryClient } from "@tanstack/react-query";

//TYPESCRIPT

//ICONS

import {
  EscapeRoomQuestion,
  Hotspot,
  Popup,
  QuizData,
  Scene,
  TourData,
  question,
} from "../../../Types";
import { UseSaveTour } from "../../../api/calls/Queries.ts";

import OtherHotspots from "./OtherHotspots.tsx";
import {
  useCurrentSceneStore,
  useEditingElementStore,
} from "../../../api/Zustand.ts";
import { QuizAddIcon } from "../../Icons/left-side-bar-icons.tsx";
import { EditPencil, Trashbin } from "../../Icons/gui-elements-icons.tsx";
import { getLanguageFile } from "../../common/GetLanguageFile.ts";

function QuizEditing(props: { id: string; tourID: number }) {
  const { id, tourID } = props;

  const { currentSceneID } = useCurrentSceneStore();

  const { setEditingElem } = useEditingElementStore();

  const { mutateAsync: saveTour, isPending: isSaving } = UseSaveTour();

  const queryClient = useQueryClient();

  const { data: tourData } = useQuery<TourData>({
    queryKey: ["GetTour", tourID],
  });
  const { data: quizData } = useQuery<QuizData>({
    queryKey: ["GetQuiz", tourData?.assignedQuizID],
  });

  const currentQuizHs: Hotspot | undefined = React.useMemo(() => {
    const temp = tourData?.scenes
      .find((one) => one.id === currentSceneID)
      ?.hotspots.find((one) => one.id === id);
    return temp;
  }, [tourData, currentSceneID, id]);

  const handlePosEdit = () => {
    queryClient.setQueryData(["GetTour", tourID], {
      ...tourData,
      scenes: tourData?.scenes.map((sc: Scene) =>
        sc.id === currentSceneID
          ? {
              ...sc,
              hotspots: sc.hotspots.map((hs: Hotspot) =>
                hs.id === id
                  ? {
                      ...hs,
                      EDIT_POS: !tourData?.scenes
                        ?.find((one: Scene) => one.id === currentSceneID)
                        ?.hotspots.find((one: Hotspot) => one.id === id)!
                        .EDIT_POS,
                    }
                  : hs
              ),
            }
          : sc
      ),
    });
  };
  const onQuestionChange = React.useCallback(
    (ev: React.ChangeEvent<HTMLSelectElement>) => {
      queryClient.setQueryData(["GetTour", tourID], (oldData: TourData) =>
        oldData
          ? {
              ...oldData,
              scenes: oldData?.scenes.map((sc: Scene) =>
                sc.id === currentSceneID
                  ? {
                      ...sc,
                      hotspots: sc.hotspots.map((hs: Hotspot) =>
                        hs.id === id
                          ? {
                              ...hs,
                              target: JSON.parse(ev.target.value),
                            }
                          : hs
                      ),
                    }
                  : sc
              ),
            }
          : oldData
      );
    },
    [currentSceneID, id, queryClient, tourID]
  );

  const handleDelete = React.useCallback(() => {
    const tempSceneSpots = tourData!.scenes
      .find((one) => one.id === currentSceneID)!
      .hotspots.filter((spot) => spot.id !== id);

    const tempScenePops = tourData!.popups.filter((pop) => pop.id !== id);
    const newTourData = queryClient.setQueryData(["GetTour", tourID], {
      ...tourData,
      scenes: tourData!.scenes.map((scene) =>
        scene.id === currentSceneID
          ? { ...scene, hotspots: tempSceneSpots }
          : scene
      ),

      popups: tempScenePops,
    });

    saveTour(newTourData as TourData);

    return setEditingElem("scene", currentSceneID);
  }, [
    currentSceneID,
    id,
    queryClient,
    saveTour,
    setEditingElem,
    tourData,
    tourID,
  ]);

  const currentQuestion: question | undefined = React.useMemo(() => {
    const hsTarget: number = tourData?.scenes
      ?.find((one: Scene) => one.id === currentSceneID)
      ?.hotspots.find((one: Hotspot) => one.id === id)?.target as number;

    if (tourData?.quizData) {
      const currentQuestion: question | undefined = tourData.quizData.data.find(
        (one) => one.questionId === hsTarget
      );
      return currentQuestion;
    }
  }, [currentSceneID, id, tourData?.scenes, tourData?.quizData]);

  const currentQuestionType: string | undefined = React.useMemo(() => {
    switch (currentQuestion?.questionTypeId) {
      case 1:
        return getLanguageFile("right_panel.single_ans");
      case 2:
        return getLanguageFile("right_panel.multi_ans");
      case 3:
        return getLanguageFile("right_panel.true_false");
      case 4:
        return getLanguageFile("right_panel.survey");
      default:
        return undefined;
    }
  }, [currentQuestion?.questionTypeId]);

  const onFlagTypeChange = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      try {
        const type = parseInt(e.target.value);

        return queryClient.setQueryData(
          ["GetTour", tourID],
          (oldData: TourData) =>
            oldData
              ? {
                  ...oldData,
                  quizData: {
                    ...oldData.quizData,
                    data: oldData.quizData?.data.map((qu) =>
                      qu.questionId === currentQuestion?.questionId
                        ? { ...qu, mandatory: type }
                        : qu
                    ),
                  },
                }
              : oldData
        );
      } catch (err) {
        throw err;
      }
    },
    [currentQuestion?.questionId, tourID, queryClient]
  );

  const [currentEscapeRoomQuestion, setCurrentEscapeRoomQuestion] =
    React.useState<EscapeRoomQuestion>();

  React.useEffect(() => {
    if (tourData?.escapeRoomData) {
      const question = tourData?.escapeRoomData?.escapeRoomQuestions?.find(
        (one) => one.questionID === currentQuestion?.questionId
      );
      setCurrentEscapeRoomQuestion(question);
    }
  }, [currentQuestion?.questionId, tourData?.escapeRoomData]);

  const handleSave = React.useCallback(() => {
    const newTourData = queryClient.setQueryData(
      ["GetTour", tourID],
      (oldData: TourData) =>
        oldData
          ? {
              ...oldData,
              scenes: oldData?.scenes.map((sc: Scene) =>
                sc.id === currentSceneID
                  ? {
                      ...sc,
                      hotspots: sc.hotspots.map((hs: Hotspot) =>
                        hs.id === id
                          ? {
                              ...hs,
                              EDIT_POS: undefined,
                              isNew: undefined,
                            }
                          : hs
                      ),
                    }
                  : sc
              ),
              // escapeRoomData: {
              //   ...oldData?.escapeRoomData,
              //   escapeRoomQuestions:
              //     oldData?.escapeRoomData?.escapeRoomQuestions?.map((er) =>
              //       er.questionID === currentEscapeRoomQuestion?.questionID
              //         ? currentEscapeRoomQuestion
              //         : er
              //     ),
              // },
            }
          : oldData
    );
    saveTour(newTourData as TourData);
  }, [
    // currentEscapeRoomQuestion,
    currentSceneID,
    id,
    queryClient,
    saveTour,
    tourID,
  ]);

  // queryClient.setQueryData(["GetTour", tourID], {
  //       ...tourData,
  //       escapeRoomData: {
  //         ...tourData?.escapeRoomData,
  //         escapeRoomQuestions:
  //           tourData?.escapeRoomData?.escapeRoomQuestions?.map((q) =>
  //             q.questionID === currentQuestion?.questionId
  //               ? { ...currentEscapeRoomQuestion }
  //               : q
  //           ),
  //       },
  //     });

  const addEscapeRoomQuestion = React.useCallback(() => {
    const newEscapeRoomQuestion: EscapeRoomQuestion = {
      sceneID: currentSceneID,
      questionID: currentQuestion?.questionId!,

      value: 30,
    };

    return queryClient.setQueryData(["GetTour", tourID], {
      ...tourData,
      escapeRoomData: {
        ...tourData?.escapeRoomData,
        escapeRoomQuestions: [
          ...tourData?.escapeRoomData?.escapeRoomQuestions!,
          newEscapeRoomQuestion,
        ],
      },
    });
  }, [
    currentQuestion?.questionId,
    currentSceneID,
    queryClient,
    tourData,
    tourID,
  ]);

  const editQuestionPopupPopup = React.useCallback(
    (questionId: number) => {
      queryClient.setQueryData(
        ["GetTour", tourID],

        {
          ...tourData,
          escapeRoomData: {
            ...tourData?.escapeRoomData,
            escapeRoomQuestions:
              tourData?.escapeRoomData?.escapeRoomQuestions?.map((qu) =>
                qu.questionID === currentEscapeRoomQuestion?.questionID
                  ? currentEscapeRoomQuestion
                  : qu
              ),
          },
        }
      );
      return setEditingElem("escapeRoomPops", questionId, id);
    },

    [
      tourData,
      currentEscapeRoomQuestion,
      queryClient,
      tourID,
      setEditingElem,
      id,
    ]
  );

  const createEscapeRoomAnswerPop = React.useCallback(
    (ansRight: boolean) => {
      const randNum = Math.floor(Math.random() * 10000000000);

      // @ts-ignore
      const newPop: Popup = {
        id: randNum,
        title: "test",
        type: "text",
      };
      queryClient.setQueryData(["GetTour", tourID], {
        ...tourData,
        escapeRoomData: {
          ...tourData?.escapeRoomData,
          escapeRoomQuestions:
            tourData?.escapeRoomData?.escapeRoomQuestions?.map((qu) =>
              qu.questionID === currentQuestion?.questionId
                ? ansRight
                  ? { ...qu, correctAnsPopID: randNum }
                  : { ...qu, wrongAnsPopID: randNum }
                : qu
            ),
        },
        popups: [...tourData?.popups!, newPop],
      });

      return setEditingElem("escapeRoomPops", randNum, id);
    },
    [
      queryClient,
      tourData,
      tourID,
      currentQuestion?.questionId,
      setEditingElem,
      id,
    ]
  );

  const changeFinalAndInitialStates = React.useCallback(
    (initial: boolean, value: boolean) => {
      return queryClient.setQueryData(["GetTour", tourID], {
        ...tourData,
        escapeRoomData: {
          ...tourData?.escapeRoomData,
          escapeRoomQuestions:
            tourData?.escapeRoomData?.escapeRoomQuestions?.map((qu) =>
              qu.questionID === currentQuestion?.questionId
                ? initial
                  ? { ...qu, isFirst: value }
                  : { ...qu, isFinal: value }
                : qu
            ),
        },
      });
    },
    [currentQuestion?.questionId, queryClient, tourData, tourID]
  );

  const isFirstIsDisabled = React.useMemo(() => {
    const otherQuestions =
      tourData?.escapeRoomData?.escapeRoomQuestions?.filter(
        (qu) => qu.questionID !== currentEscapeRoomQuestion?.questionID
      );

    const a = otherQuestions?.find((one) => one.isFirst);

    return !!a || currentEscapeRoomQuestion?.isFinal;
  }, [
    currentEscapeRoomQuestion?.isFinal,
    currentEscapeRoomQuestion?.questionID,
    tourData?.escapeRoomData?.escapeRoomQuestions,
  ]);

  const isFinalIsDisabled = React.useMemo(() => {
    const otherQuestions =
      tourData?.escapeRoomData?.escapeRoomQuestions?.filter(
        (qu) => qu.questionID !== currentEscapeRoomQuestion?.questionID
      );

    const a = otherQuestions?.find((one) => one.isFinal);

    return !!a || currentEscapeRoomQuestion?.isFirst;
  }, [
    currentEscapeRoomQuestion?.isFirst,
    currentEscapeRoomQuestion?.questionID,
    tourData?.escapeRoomData?.escapeRoomQuestions,
  ]);

  const changeQuestionValue = React.useCallback(
    (value: number) => {
      return queryClient.setQueryData(["GetTour", tourID], {
        ...tourData,
        escapeRoomData: {
          ...tourData?.escapeRoomData,
          escapeRoomQuestions:
            tourData?.escapeRoomData?.escapeRoomQuestions?.map((qu) =>
              qu.questionID === currentQuestion?.questionId
                ? { ...qu, value: value }
                : qu
            ),
        },
      });
    },
    [queryClient, currentQuestion?.questionId, tourData, tourID]
  );

  return (
    <div className="right-side-panel-editor">
      {quizData && tourData?.assignedQuizID ? (
        <>
          {tourData?.scenes
            ?.find((one: Scene) => one.id === currentSceneID)
            ?.hotspots.find((one: Hotspot) => one.id === id) && (
            <>
              <h3>QUIZ</h3>
              {quizData?.data && (
                <label>
                  {getLanguageFile("right_panel.question")}
                  <select
                    required
                    value={currentQuizHs?.target || ""}
                    onChange={onQuestionChange}
                  >
                    <option disabled value={""}>
                      {getLanguageFile("right_panel.question_placeholder")}
                    </option>

                    {quizData.data.map((question: question) => (
                      <option
                        key={question.questionId}
                        value={question.questionId}
                      >
                        {question.questionText.substring(0, 30)}
                        {question.questionText.length > 30 && "..."}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <p>
                <b> {getLanguageFile("right_panel.question_type")}</b>
                <br />
                {currentQuestion && currentQuestionType}
              </p>

              {/* <label className="debug">
                <pre>FLG_QUESTION_TYPE</pre>
                <select
                  onChange={onFlagTypeChange}
                  value={currentQuestion?.flgObbligatoria}
                >
                  <option value={0}>0</option>
                  <option value={1}>1</option>
                  <option value={2}>dom</option>
                </select>
              </label> */}

              <button
                className={currentQuizHs?.EDIT_POS ? "active" : undefined}
                id="edit"
                onClick={handlePosEdit}
              >
                {tourData?.scenes
                  ?.find((one: Scene) => one.id === currentSceneID)
                  ?.hotspots.find((one: Hotspot) => one.id === id)!.EDIT_POS ? (
                  getLanguageFile("right_panel.save_position")
                ) : (
                  <>
                    {getLanguageFile("right_panel.edit_position")}{" "}
                    <EditPencil />
                  </>
                )}
              </button>
              {tourData.escapeRoomData?.escapeRoomType === (1 || 2 || 3) && (
                <div>
                  <div className="category-divider">
                    <h4>Escape room</h4>
                    <hr></hr>
                  </div>
                  <div className="input-range-inline">
                    value {currentEscapeRoomQuestion?.value}
                    <input
                      type="range"
                      value={currentEscapeRoomQuestion?.value}
                      onChange={(e) =>
                        changeQuestionValue(e.target.valueAsNumber)
                      }
                      max={100}
                      min={0}
                    />
                  </div>

                  {currentEscapeRoomQuestion ? (
                    <>
                      <div>
                        <label>
                          Initial
                          <input
                            disabled={isFirstIsDisabled}
                            checked={currentEscapeRoomQuestion.isFirst}
                            onChange={(e) =>
                              changeFinalAndInitialStates(
                                true,
                                e.target.checked
                              )
                            }
                            type="checkbox"
                          ></input>
                        </label>
                        <label>
                          Final
                          <input
                            disabled={isFinalIsDisabled}
                            checked={currentEscapeRoomQuestion.isFinal}
                            onChange={(e) =>
                              changeFinalAndInitialStates(
                                false,
                                e.target.checked
                              )
                            }
                            type="checkbox"
                          ></input>
                        </label>

                        {getLanguageFile("right_panel.right_ans_action")}
                      </div>
                      Popup{" "}
                      <button
                        onClick={() => {
                          if (currentEscapeRoomQuestion?.correctAnsPopID) {
                            editQuestionPopupPopup(
                              currentEscapeRoomQuestion.correctAnsPopID
                            );
                          } else {
                            createEscapeRoomAnswerPop(true);

                            // editQuestionPopupPopup(test);
                          }
                        }}
                      >
                        Edit
                      </button>{" "}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: ".5rem",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={
                            !!currentEscapeRoomQuestion?.correctAnsSceneID
                          }
                          onChange={(e) => {
                            console.debug(e.target.checked);

                            const { checked } = e.target;

                            if (checked) {
                              setCurrentEscapeRoomQuestion({
                                ...currentEscapeRoomQuestion,
                                //@ts-ignore

                                correctAnsSceneID: tourData.scenes[0].id,
                              });
                            } else {
                              //@ts-ignore

                              setCurrentEscapeRoomQuestion({
                                ...currentEscapeRoomQuestion,
                                //@ts-ignore
                                correctAnsSceneID: undefined,
                              });
                            }
                          }}
                        ></input>
                        <label>Navigate to</label>
                        <select
                          disabled={
                            !currentEscapeRoomQuestion?.correctAnsSceneID
                          }
                          style={{ flex: 1 }}
                          value={currentEscapeRoomQuestion?.correctAnsSceneID}
                          onChange={(e) =>
                            setCurrentEscapeRoomQuestion({
                              ...currentEscapeRoomQuestion,
                              // @ts-expect-error
                              correctAnsSceneID: e.target.value,
                            })
                          }
                        >
                          {tourData.scenes
                            .filter((sc) => sc.id !== currentSceneID)
                            .map((sc) => (
                              <option key={sc.id} value={sc.id}>
                                {sc.title}
                              </option>
                            ))}
                        </select>
                      </div>
                      <br></br>
                      <div>
                        {getLanguageFile("right_panel.wrong_ans_action")}
                      </div>
                      Popup{" "}
                      <button
                        onClick={() => {
                          if (currentEscapeRoomQuestion?.wrongAnsPopID) {
                            editQuestionPopupPopup(
                              currentEscapeRoomQuestion.wrongAnsPopID
                            );
                          } else {
                            createEscapeRoomAnswerPop(false);

                            // editQuestionPopupPopup(test);
                          }
                        }}
                      >
                        Edit
                      </button>{" "}
                      {JSON.stringify(currentEscapeRoomQuestion?.wrongAnsPopID)}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: ".5rem",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={!!currentEscapeRoomQuestion?.wrongAnsSceneID}
                          onChange={(e) => {
                            const value = e.target.checked;

                            if (value) {
                              setCurrentEscapeRoomQuestion({
                                ...currentEscapeRoomQuestion,
                                //@ts-ignore

                                wrongAnsSceneID: tourData.scenes[0].id,
                              });
                            } else {
                              //@ts-ignore

                              setCurrentEscapeRoomQuestion({
                                ...currentEscapeRoomQuestion,
                                //@ts-ignore
                                wrongAnsSceneID: undefined,
                              });
                            }
                          }}
                        ></input>
                        <label>Navigate to</label>
                        <select
                          disabled={!currentEscapeRoomQuestion?.wrongAnsSceneID}
                          style={{ flex: 1 }}
                          value={currentEscapeRoomQuestion?.wrongAnsSceneID}
                          onChange={(e) =>
                            setCurrentEscapeRoomQuestion({
                              ...currentEscapeRoomQuestion,
                              // @ts-expect-error
                              wrongAnsSceneID: e.target.value,
                            })
                          }
                        >
                          {tourData.scenes
                            .filter((sc) => sc.id !== currentSceneID)
                            .map((sc) => (
                              <option key={sc.id} value={sc.id}>
                                {sc.title}
                              </option>
                            ))}
                        </select>
                      </div>
                    </>
                  ) : (
                    <div>
                      <button onClick={addEscapeRoomQuestion}>Add</button>
                    </div>
                  )}
                </div>
              )}
              <div style={{ display: "flex" }}>
                <button
                  id="save"
                  disabled={isSaving || !currentQuizHs?.target}
                  style={{ flex: 1 }}
                  onClick={handleSave}
                >
                  {currentQuizHs?.isNew
                    ? getLanguageFile("generics.create")
                    : isSaving
                    ? getLanguageFile("generics.saving")
                    : getLanguageFile("generics.save")}
                </button>

                <button id="delete" style={{ flex: 1 }} onClick={handleDelete}>
                  {tourData?.scenes
                    ?.find((one: Scene) => one.id === currentSceneID)
                    ?.hotspots.find((one: Hotspot) => one.id === id)!.isNew ? (
                    getLanguageFile("generics.cancel")
                  ) : (
                    <>
                      {getLanguageFile("generics.delete")} <Trashbin />
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </>
      ) : (
        <div
          style={{
            marginTop: "50%",
            textAlign: "center",
          }}
        >
          <h3 style={{ fontSize: "1.25rem" }}>
            {getLanguageFile("right_panel.no_quiz_tour")}{" "}
          </h3>
          <div style={{ display: "flex" }}>
            <button
              id="delete"
              style={{ flex: 1, background: "" }}
              onClick={handleDelete}
            >
              {getLanguageFile("generics.delete")}
            </button>
          </div>
        </div>
      )}

      <OtherHotspots
        currentHotspot={currentQuizHs!}
        icon={<QuizAddIcon width="1em" height={"1em"} fill="#fff" />}
        tourData={tourData!}
        type="quiz"
      ></OtherHotspots>
    </div>
  );
}

export default QuizEditing;
